import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useListQuery } from '@/hooks/useListQuery';
import { financeRefetchInterval, useFinanceStreamState } from '@/hooks/useFinanceStreamState';
import { useAuth } from '@/hooks/useAuth';
import { playNotifySound } from '@/lib/notify-sound';

const PENDING_QUERY = { page: 1, pageSize: 1, status: 'pending' } as const;

function cap(n: number): string {
  return n > 99 ? '99+' : String(n);
}

export function PendingRequestsNav() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canSeeDeposits = hasPermission('deposit:read');
  const canSeeWithdraws = hasPermission('withdraw:read');

  // These two counts ARE the alert an operator watches from any page in the
  // backoffice, and until #292 the WebSocket was the only thing that moved
  // them. A refused or dropped socket froze both badges at whatever they read
  // when the page loaded, with nothing to say so.
  // One subscription to the stream store, read twice — the pill's interval and
  // the alert's "is the socket carrying this already" are the same fact.
  const stream = useFinanceStreamState();
  const refetchInterval = financeRefetchInterval(stream);

  const deposits = useListQuery<unknown>(
    canSeeDeposits ? '/api/v1/deposits' : '',
    PENDING_QUERY,
    { refetchInterval },
  );
  const withdraws = useListQuery<unknown>(
    canSeeWithdraws ? '/api/v1/withdraws' : '',
    PENDING_QUERY,
    { refetchInterval },
  );

  const depositCount = deposits.data?.total ?? 0;
  const withdrawCount = withdraws.data?.total ?? 0;

  // The audible alert, on the polling path (issue #292's third acceptance
  // criterion). While the socket is live it already fires from the stream
  // handler on `*.created`, so this would double it; while the socket is not,
  // nothing fires at all and a new request lands in silence.
  //
  // A RISE in the pending count is the signal: an approval or a rejection can
  // only lower it, so this can never echo the operator's own action back at
  // them.
  //
  // Each queue seeds on ITS OWN first success, and a queue that has not answered
  // yet is not a queue with zero rows. The two requests land in different
  // macrotasks on a real network, so a shared baseline taken when EITHER
  // resolved recorded the other as `?? 0` and then heard its real count as a
  // rise — a beep on most hard page loads, for nothing.
  const lastDeposit = useRef<number | null>(null);
  const lastWithdraw = useRef<number | null>(null);
  const live = stream.status === 'live';

  useEffect(() => {
    if (!deposits.isSuccess) return;
    const previous = lastDeposit.current;
    lastDeposit.current = depositCount;
    if (previous === null || live) return;
    if (depositCount > previous) playNotifySound();
  }, [depositCount, live, deposits.isSuccess]);

  useEffect(() => {
    if (!withdraws.isSuccess) return;
    const previous = lastWithdraw.current;
    lastWithdraw.current = withdrawCount;
    if (previous === null || live) return;
    if (withdrawCount > previous) playNotifySound();
  }, [withdrawCount, live, withdraws.isSuccess]);

  return (
    <>
      {canSeeDeposits && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('dashboard.pendingDeposits')}
          title={t('dashboard.pendingDeposits')}
          className="relative"
          onClick={() => navigate('/finance?tab=deposits')}
        >
          <ArrowDownToLine className="h-5 w-5 text-emerald-500" />
          {depositCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-semibold leading-none text-white">
              {cap(depositCount)}
            </span>
          )}
        </Button>
      )}
      {canSeeWithdraws && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('dashboard.pendingWithdraws')}
          title={t('dashboard.pendingWithdraws')}
          className="relative"
          onClick={() => navigate('/finance?tab=withdraws')}
        >
          <ArrowUpFromLine className="h-5 w-5 text-amber-500" />
          {withdrawCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold leading-none text-white">
              {cap(withdrawCount)}
            </span>
          )}
        </Button>
      )}
    </>
  );
}
